/**
 * Shared error classification for TanStack Query.
 *
 * Ported verbatim (behavior-preserving) from the bespoke `useCache` hook so the
 * two systems agree during the migration (epic #2266). The UI reads
 * `error.classification.userMessage`, so this shape must be preserved when the
 * query layer surfaces errors.
 */

/**
 * @typedef {Object} ErrorClassification
 * @property {'gateway'|'network'|'timeout'|'auth'|'rateLimit'|'server'|'validation'|'unknown'} type
 * @property {'low'|'medium'|'high'} severity
 * @property {boolean} retryable
 * @property {string} userMessage
 */

/**
 * Classify an error into a type/severity/retryable/userMessage bundle.
 * @param {(Error & { isTimeout?: boolean, isHtmlError?: boolean, userMessage?: string }) | null | undefined} error
 * @returns {ErrorClassification}
 */
export function classifyError(error) {
  const classification = {
    type: 'unknown',
    severity: 'medium',
    retryable: false,
    userMessage: 'An unexpected error occurred'
  };

  if (!error) return classification;

  const message = error.message?.toLowerCase() || '';
  const name = error.name?.toLowerCase() || '';

  // Gateway timeout errors (504/502)
  if (
    error.isTimeout ||
    error.isHtmlError ||
    message.includes('gateway timeout') ||
    message.includes('504') ||
    message.includes('502') ||
    message.includes('bad gateway')
  ) {
    classification.type = 'gateway';
    classification.severity = 'high';
    classification.retryable = true;
    classification.userMessage = error.userMessage || 'Server is temporarily unavailable. Please try again.';
  }
  // Network errors
  else if (message.includes('network') || message.includes('fetch') || name === 'typeerror') {
    classification.type = 'network';
    classification.severity = 'high';
    classification.retryable = true;
    classification.userMessage = 'Network connection issue. Please check your internet connection.';
  }
  // Timeout errors
  else if (message.includes('timeout') || message.includes('aborted')) {
    classification.type = 'timeout';
    classification.severity = 'medium';
    classification.retryable = true;
    classification.userMessage = 'Request timed out. Please try again.';
  }
  // Authorization errors
  else if (message.includes('unauthorized') || message.includes('403') || message.includes('401')) {
    classification.type = 'auth';
    classification.severity = 'high';
    classification.retryable = false;
    classification.userMessage = 'Authentication required. Please refresh the page.';
  }
  // Rate limit errors
  else if (message.includes('429') || message.includes('too many requests') || message.includes('rate limit')) {
    classification.type = 'rateLimit';
    classification.severity = 'high';
    classification.retryable = false;
    classification.userMessage = 'Too many requests. Please wait a moment.';
  }
  // Server errors
  else if (message.includes('500') || message.includes('internal server')) {
    classification.type = 'server';
    classification.severity = 'high';
    classification.retryable = true;
    classification.userMessage = 'Server error. Please try again later.';
  }
  // Validation errors
  else if (message.includes('validation') || message.includes('invalid')) {
    classification.type = 'validation';
    classification.severity = 'low';
    classification.retryable = false;
    classification.userMessage = 'Invalid data provided.';
  }

  return classification;
}

/**
 * Exponential backoff with jitter — matches `useCache`'s retryWithBackoff.
 * @param {number} attempt - Zero-based retry attempt index
 * @returns {number} delay in milliseconds
 */
export function backoffDelay(attempt) {
  const baseDelay = 1000;
  return baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
}

/**
 * TanStack Query `retry` predicate: retry only retryable classifications, and
 * cap at `maxRetries` attempts.
 *
 * TanStack calls this with a 0-based `failureCount` and its numeric `retry: N`
 * default retries while `failureCount < N`, so we stop at `>= maxRetries` to
 * match "N retries" (3 by default, mirroring useCache).
 * @param {number} failureCount - Failures so far (0-based per TanStack)
 * @param {Error} error
 * @param {number} [maxRetries=3]
 * @returns {boolean}
 */
export function shouldRetry(failureCount, error, maxRetries = 3) {
  if (failureCount >= maxRetries) return false;
  return classifyError(error).retryable;
}
